import React, {useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import {ProgressDialog, TextDialog} from "../dialogs";
import CloseIcon from '@material-ui/icons/Close';
import CheckIcon from '@material-ui/icons/Check';
import EditIcon from '@material-ui/icons/Edit';
import WarningIcon from '@material-ui/icons/Warning';
import PriorityHighIcon from '@material-ui/icons/PriorityHigh';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import AddToPhotosIcon from '@material-ui/icons/AddToPhotos';
import DeleteIcon from '@material-ui/icons/Delete';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import {
  Card,
  CardHeader,
  CircularProgress,
  Collapse,
  DialogContent,
  List,
  ListItem,
  Typography
} from '@material-ui/core';
import { DraggableDialog, PromptDialog } from '../dialogs';
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import { app, dialog, ipcRenderer, fs } from '../electron-modules';
import { useSnackbar } from 'notistack';
import {changeSwitched} from "../redux/settings-slice";
import { textToColor } from '../viewer/js/labelling/categories';
import { ArrowTooltip } from '../tooltips';
import { DenseDivider, IconToolBar, ToolBarButton } from '../toolbar';
import slash from 'slash';
import { PythonExecutable } from '../executable';
import { Viewer } from '../viewer/js/main';
import LocalScene from '../viewer/js/projections';
import { FixedSizeList as DynamicList } from "react-window";
import { LinearProgressWithLabel } from '../progress';
import path from 'path';
import { EnhancedIconButton } from '../buttons';

const labelExtension = "3dl";
const modelExtension = "pth";
const dxfExtension = "dxf";
const labelFilter = [{name: 'Engine Label File', extensions: [labelExtension]}];
const modelFilter = [{name: 'Engine Trained Model File', extensions: [modelExtension]}];
const dxfFilter = [{name: 'DXF File', extensions: [dxfExtension]}];
const labelHeight = 46;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    titleClose: {
      position: 'absolute',
      right: theme.spacing(0),
      color: theme.palette.grey[500],
    },
    titleInfo: {
      position: 'absolute',
      right: theme.spacing(4),
      marginTop: theme.spacing(0),
      color: theme.palette.grey[500],
    },
    titleMin: {
      position: 'absolute',
      right: theme.spacing(4),
      marginTop: theme.spacing(-1),
      color: theme.palette.grey[500],
    },
    userWarning: {
      padding: theme.spacing(3)
    },
    box: {
      padding: theme.spacing(4)
    },
    formControl: {
      width: '100%'
    },
    categoryName: {
      flex: 1
    },
    success: {
      color: theme.palette.success.main
    },
    warning: {
      color: theme.palette.warning.main
    },
    error: {
      color: theme.palette.error.main
    },
    info: {
      color: theme.palette.info.main
    },
    folderList: {
      padding: "0px",
      overflow: "auto",
      flex: 1,
      alignItems: "center",
      justifyContent: "center"
    },
    headerTitle: {
      fontWeight: "bold",
      cursor: "pointer"
    },
    cardHeader: {
      padding: "10px"
    },
    cardButtons: {
      marginTop: 0,
      alignSelf: "center"
    },
    labelFixedHeight: {
      height: `${labelHeight}px`
    },
    labelTitle: {
      flex: 1,
      userSelect: "none"
    },
    labelTitleWarning: {
      flex: 1,
      userSelect: "none",
      color: theme.palette.warning.main
    },
    labelTitleError: {
      flex: 1,
      userSelect: "none",
      color: theme.palette.error.main
    },
    card: {
      margin: '10px',
      userSelect: "none"
    },
    progressToolbar: {
      display: "flex",
      marginLeft: "12px",
      marginRight: "12px"
    },
    toolbarLinear: {
      margin: "auto 12px"
    },
    toolbarProgressCenter: {
      top: "50%",
      height: "6px",
      marginTop: "-3px"
    },
    downloadMargin: {
      marginTop: theme.spacing(1)
    }
  }),
);

const Label = (props) => {
  const viewer = props.viewer as Viewer;
  const {label, index, isScrolling, style} = props;

  const classes = useStyles();
  const selected = label.selected;
  const completed = label.completed;
  const hasEnoughPoints = label.hasEnoughPoints;

  let labelTitle = label.highlighted ? classes.labelTitleWarning : classes.labelTitle;

  const selectLabel = (id) => {
    viewer?.selectLabel(id);
  };

  const completeLabel = (id) => {
    viewer?.completeLabel(id);
  };

  const deleteLabel = (id) => {
    viewer?.deleteLabel(id);
  };

  const zoomToLabel = (id) => {
    viewer?.zoomToLabel(id);
  };

  const unHighlight = (id) => {
    viewer?.unHighlight(id);
  };

  return (
    <ListItem selected={selected} style={style} className={classes.labelFixedHeight}>
      <Typography variant="caption" className={label.valid? labelTitle: classes.labelTitleError}>
        {`Label #${index + 1} (${label.numPoints})`}
      </Typography>

      {isScrolling && (<React.Fragment>
        <Typography variant="caption">
          Loading...
        </Typography>
      </React.Fragment>)}

      {!isScrolling && (<React.Fragment>

        {/* Complete Label */}
        <EnhancedIconButton
          title="Complete Label"
          visible={selected && hasEnoughPoints}
          onClick={() => {
            completeLabel(label.id);
          }}
        >
          <CheckIcon
            fontSize="small"
            className={classes.success}
          />
        </EnhancedIconButton>

        {/* Warning on Label */}
        <EnhancedIconButton
          title="Label is invalid, will not be exported"
          visible={completed && (!label.valid)}
          onClick={() => {
            completeLabel(label.id);
          }}
        >
          <WarningIcon fontSize="small" className={classes.error}/>
        </EnhancedIconButton>

        {/* Highlighted Label */}
        <EnhancedIconButton
          title="Label was highlighted, click to remove highlight"
          visible={completed && (label.highlighted)}
          onClick={() => {
            unHighlight(label.id);
          }}
        >
          <PriorityHighIcon fontSize="small" className={classes.warning}/>
        </EnhancedIconButton>

        {/* Edit Label */}
        <EnhancedIconButton
          title="Edit Label"
          visible={completed}
          onClick={() => {
            selectLabel(label.id);
          }}
        >
          <EditIcon fontSize="small"/>
        </EnhancedIconButton>

        {/* Zoom to extent */}
        <EnhancedIconButton
          title="Zoom to Label"
          visible={completed}
          onClick={() => {
            zoomToLabel(label.id);
          }}
        >
          <ZoomInIcon fontSize="small"/>
        </EnhancedIconButton>

        {/* Delete Label */}
        <EnhancedIconButton
          title="Delete Label"
          onClick={() => {
            deleteLabel(label.id);
          }}
        >
          <CloseIcon fontSize="small" className={classes.error}/>
        </EnhancedIconButton>

      </React.Fragment>)}

    </ListItem>
  );
};

const Category = (props) => {
  const viewer = props.viewer as Viewer;
  const {category, isValidCategory} = props;

  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);
  const [editPrompt, setEditPrompt] = useState(false);
  const [deletePrompt, setDeletePrompt] = useState(false);

  const formatName = (name) => {
    return (name.length > 12) ? name.substr(0,11) + "..." : name;
  };

  const addThenExpand = (categoryID) => {
    addLabel(categoryID);
    setExpanded(true);
  };

  const addLabel = (id) => {
    viewer?.addLabel(id);
  };

  const handleDeleteCategory = () => {
    viewer?.deleteCategory(category.id);
    setDeletePrompt(false);
  };

  const handleEditCategory = (name) => {
    if (!isValidCategory(name)) {
      return;
    }

    viewer?.editCategoryName(category.id, name);
    setEditPrompt(false);
  };

  const activeLabel = () => {
    return viewer?.checkIfSelected();
  };

  const addNewLabelDisabled = activeLabel();
  const addNewlabelTooltip = `
    Add new label
    <br>Press space to add new
    <br>label to ${viewer.currentCategory()} category
  `;

  const color = textToColor(category.name);
  const scrollingDivLength = category.labels.length;
  const useScrollingDiv = scrollingDivLength > 100;
  const scrollingDivHeight = Math.min(scrollingDivLength*labelHeight, 250);

  return (
    <React.Fragment>
      <Card className={classes.card}>
        {/* Header */}
        <CardHeader
          className={classes.cardHeader}
          classes={{
            action: classes.cardButtons
          }}
          action={
            <React.Fragment>
              {/* Expand/collapse measurement */}
              <EnhancedIconButton
                title={expanded? "Collapse" : "Expand"}
                onClick={(event) => {
                  event.stopPropagation();
                  setExpanded(!expanded);
                }}
              >
                {expanded && <ExpandLessIcon fontSize="small"/>}
                {!expanded && <ExpandMoreIcon fontSize="small"/>}
              </EnhancedIconButton>

              {/* Edit category name */}
              <EnhancedIconButton
                title="Edit Name"
                onClick={() => {
                  setEditPrompt(true);
                }}
              >
                <EditIcon fontSize="small"/>
              </EnhancedIconButton>

              {/* Add new label */}
              <EnhancedIconButton
                title={addNewlabelTooltip}
                disabled={addNewLabelDisabled}
                onClick={() => {
                  addThenExpand(category.id);
                }}
              >
                <AddToPhotosIcon fontSize="small"/>
              </EnhancedIconButton>

              {/* Delete category */}
              <EnhancedIconButton
                title="Delete class"
                onClick={() => {
                  setDeletePrompt(true);
                }}
              >
                <DeleteIcon fontSize="small"/>
              </EnhancedIconButton>

            </React.Fragment>
          }
          disableTypography={true}
          title={
            <Typography
              variant="body2"
              style={{color}}
              className={classes.headerTitle}
            >
              {formatName(category.name)}
            </Typography>
          }
          subheader={
            <Typography
              variant="caption"
            >
              {`Total Labels: ${category.labels.length}`}
            </Typography>
          }
        />

        {/* Expandable content */}
        <Collapse in={expanded}>
          {/* Use normal list for small labels list */}
          {!useScrollingDiv && (<React.Fragment>
            <List className={classes.folderList}>
              {category.labels.map((label, index) =>
                <Label
                  key={label.id}
                  viewer={viewer}
                  isScrolling={false}
                  label={label}
                  index={index}
                />
              )}
            </List>
          </React.Fragment>)}

          {/* Use scrolling list for large amounts of labels */}
          {expanded && useScrollingDiv && (<React.Fragment>
            <DynamicList
              useIsScrolling
              className={classes.formControl}
              height={scrollingDivHeight}
              itemCount={scrollingDivLength}
              itemSize={labelHeight}
            >
              {({index, isScrolling, style}) => (
                <Label
                  key={category.id}
                  style={style}
                  viewer={viewer}
                  isScrolling={isScrolling}
                  label={category.labels[index]}
                  index={index}
                />
              )}
            </DynamicList>
          </React.Fragment>)}
        </Collapse>

      </Card>

      <TextDialog
        open={editPrompt}
        onCancel={() => setEditPrompt(false)}
        onSubmit={handleEditCategory}
        title="Edit Class"
        prompt="Enter new class name"
        default={category.name}
        label="Class Name"
        clear={true}
      />

      <PromptDialog
        open={deletePrompt}
        onCancel={() => setDeletePrompt(false)}
        onSubmit={handleDeleteCategory}
        title="Delete Class?"
        prompt={`Do you want to delete this class (${category.name})? All labels will be removed`}
        button="Delete"
      />
    </React.Fragment>
  );
};

export const ImageLabelling = (props) => {
  const viewer = props.viewer as Viewer;
  const {setDefaultTab, setTabsEnabled, categoryList,
    imageLabelOpen, setImageLabelOpen} = props;

  const classes = useStyles();
  const showMessage = useSnackbar().enqueueSnackbar;
  const dispatch = useDispatch();

  const [labelType, setLabelType] = useState(null);
  const [minimized, setMinimized] = useState(null);
  const [addCategory, setAddCategory] = useState(false);
  const [closePrompt, setClosePrompt] = useState(false);
  const [importData, setImportData] = useState(null);
  const [modalName, setModalName] = useState("");
  const [labelLoadProgress, setLabelLoadProgress] = useState(0);
  const [trainClassifyProgress, setTrainClassifyProgress] = useState(0);
  const [trainClassifyExe, setTrainClassifyExe] = useState(null);
  const [predOrTrain, setPredOrTrain] = useState("");
  const [classification, setClassification] = useState(false);
  const [training, setTraining] = useState(false);
  const [trainingURL, setTrainingURL] = useState(null);
  const [downloadingResources, setDownloadingResources] = useState(false);
  const [trainingPrompt, setTrainingPrompt] = useState(false);
  const [classificationPrompt, setClassificationPrompt] = useState(false);

  const isOrthoLabel = labelType === "ortho";
  const isSceneLabel = labelType === "scene";
  const importPrompt = importData !== null;
  const importCategories = importData?.categories.map(x => x.name);

  let numImportLabels = 0;
  importData?.annotations.forEach(label => {
    numImportLabels += label.segmentation.length;
  });

  const resetExecutableValues = () => {
    setTrainClassifyProgress(0);
    setTrainingURL(null);
    setDownloadingResources(false);
    setTraining(false);
    setClassification(false);
  };

  const handleOpen = (type) => {
    setImageLabelOpen(true);
    setLabelType(type);
  };

  const handleClose = () => {
    setImageLabelOpen(false);
    handleCancelProgress();
  };

  const handleImport = async () => {
    await viewer.loadImageLabelData(importData, setLabelLoadProgress);
    showMessage(`Labels Loaded Successfully...`, {
      variant: "success"
    });

    setImportData(null);
  };

  const handleMinimize = () => {
    setMinimized(true);
  };

  const checkClose = () => {
    if (isModified) {
      setClosePrompt(true);
    } else {
      handleClose();
    }
  };

  const isValidCategory = (name) => {
    // Uniqueness check
    const isUnique = viewer?.checkCategoryUniqueness(name);
    if (!isUnique) {
      showMessage(`Class already exists`, {
        variant: "error"
      });
      return false;
    }

    // Alphanumeric, dash and underscore
    const alphanumeric = /^[a-zA-Z0-9-_]+$/;
    if (!alphanumeric.test(name)) {
      showMessage(`Invalid class name. Allowed characters are: [a-z, 0-9, _, -]`, {
        variant: "error"
      });
      return false;
    }

    return true;
  };

  const validIterations = (iterations) => {
    const numeric = /^[0-9]+$/;
    let validChar = numeric.test(iterations);
    let iterNumber = parseInt(iterations);

    let minIterations = 10;
    let maxIterations = 8192;
    let validNumber = (iterNumber >= minIterations)
      && (iterNumber <= maxIterations);

    if (!validChar || ! validNumber) {
      showMessage(`Invalid iterations. Allowed characters are: [0-9], must be between ${minIterations}-${maxIterations}`, {
        variant: "error"
      });
      return false;
    }

    return true;
  };

  const validScore = (score) => {
    const numeric = /^[0-9-.]+$/;
    let validChar = numeric.test(score);
    let scoreNumber = parseFloat(score);
    let validNumber = (scoreNumber >= 0.0 && scoreNumber <= 1.0);

    if (!validChar || ! validNumber) {
      showMessage(`Invalid score. Allowed characters are: [0-9], must be between 0.0-1.0`, {
        variant: "error"
      });
      return false;
    }

    return true;
  };

  const handleCategoryChange = (state) => {
    setAddCategory(state);
  };

  const handleCancel = () => {
    handleCategoryChange(false);
  };

  const handleAddCategory = (name) => {
    if (!isValidCategory(name)) {
      return;
    }

    viewer?.addCategory(name);
    handleCategoryChange(false);
  };

  const handleTrainingPrompt = (iterations) => {
    let iterValid = validIterations(iterations);
    if (!iterValid){
      return;
    }

    runTraining(parseInt(iterations));
    setTrainingPrompt(false);
  };

  const handleClassificationPrompt = (score) => {
    let scoreValid = validScore(score);
    if (!scoreValid){
      return;
    }

    runClassification(parseFloat(score));
    setClassificationPrompt(false);
  };

  const handleCancelProgress = () => {
    trainClassifyExe?.destroy();
    resetExecutableValues();
  };

  const handleViewTraining = (url) => {
    ipcRenderer?.send("show-training-window", url);
  };

  const exportJSON = (categoryList) => {
    // No labels
    if (categoryList.length === 0) {
      showMessage(`No labels created to export`, {
        variant: "warning"
      });
      return;
    }

    const name = `${viewer.projectName}.${labelExtension}`;

    dialog.showSaveDialog({
      defaultPath: name,
      filters: labelFilter,
    }).then(result => {
      if (result.canceled) {
        return;
      }
      if (result.filePath) {
        let savePath = slash(result.filePath);
        let json = viewer?.labelObject();
        let text = JSON.stringify(json, null, 2);

        fs.writeFile(savePath, text, 'utf8', (err) => {
          if (err) {
            console.log(err);
            showMessage(`Failed to export labels`, {
              variant: "error"
            });
          }
          else {
            showMessage(`Image labels saved to ${path.basename(savePath)}`, {
              variant: "success"
            });
            console.log(`File written successfully to ${savePath}`);
          }
        });
      }
    });
  };

  const importImageLabels = () => {
    dialog.showOpenDialog({
      properties: ['openFile'],
      filters: labelFilter
    }).then(result => {
      if (result.canceled) {
        return;
      }

      try {
        const filePath = slash(result.filePaths[0]);
        const text = fs.readFileSync(filePath, 'utf8');
        const data = JSON.parse(text);

        if (data.type !== labelType) {
          showMessage(`Label type mismatch: "${data.type}"`, {
            variant: "error"
          });
          return;
        }

        setImportData(data);
      } catch {
        showMessage(`Error reading image labels file`, {
          variant: "error"
        });
      }
    }).catch(error => {
      console.log(error);
    });
  };

  const forceInitialProgress = () => {
    setTrainClassifyProgress(0.001);
  };

  const updateDownloadingResources = (jsonData) => {
    if (jsonData.downloading_resources) {
      setDownloadingResources(true);
    } else {
      setDownloadingResources(false);
    }
  };

  const canExportDXF = () => {
    let jsonData = viewer?.labelObject();

    const noAnnotations = jsonData.annotations.length === 0;
    const noImages = jsonData.images.length === 0;
    if (noAnnotations || noImages) {
      showMessage(`No exportable labels found. Active images are required`, {
        variant: "warning"
      });
      return false;
    }

    return true;
  };

  const runExportDXF = () => {
    // No labels
    if (categoryList.length === 0) {
      showMessage(`No labels created to export`, {
        variant: "warning"
      });
      return;
    }

    const name = `${viewer.projectName}.${dxfExtension}`;

    dialog.showSaveDialog({
      defaultPath: name,
      filters: dxfFilter,
    }).then(result => {
      if (result.canceled) {
        return;
      }
      if (result.filePath) {
        let savePath = slash(result.filePath);
        let jsonData = viewer?.labelObject();
        let text = JSON.stringify(jsonData, null, 2);
        let tempCocoPath = path.join(app.getPath('userData'), "tempLabels.3dl");

        fs.writeFile(tempCocoPath, text, 'utf8', (err) => {
          if (err) {
            console.log(err);
            showMessage(`Failed to export labels to DXF file`, {
              variant: "error"
            });
          }
        });

        let exe = new PythonExecutable();
        let pythonName = "coco_to_dxf";
        let commands = [
          "-p", pythonName,
          "--coco_path", tempCocoPath,
          "--dxf_path", savePath
        ];

        exe.run({
          command: commands,
          saveLogs: true,
          onClose: () => {
            try {
              showMessage(`DXF file saved to ${savePath}`, {
                variant: "success"
              });
            }
            catch (err) {
              console.log(err);
              showMessage("DXF file failed to save", {
                variant: "error"
              });
            }
          }
        });
      }
    }).catch(error => {
      console.log(error);
    });
  };

  const canRunClassification = () => {
    const imagePaths = viewer.loadedImageLabelPaths();
    if (imagePaths.length === 0) {
      showMessage(`No images found for classification`, {
        variant: "warning"
      });
      return false;
    }

    return true;
  };

  const runClassification = (score) => {
    if (training) {
      showMessage(`Cannot run Classification until training is complete`, {
        variant: "warning"
      });
      return;
    }

    resetExecutableValues();
    setClassification(true);

    dialog.showOpenDialog({
      properties: ['openFile'],
      filters: modelFilter
    }).then(result => {
      if (result.canceled) {
        setClassification(false);
        return;
      }

      try {
        let modelPath = slash(result.filePaths[0]);

        if (result.canceled) {
          setClassification(false);
          return;
        }

        try {
          let pythonName = "aerial_image_classification";
          let appPath = app.getPath('userData');
          let commands = [
            "-p", pythonName,
            "--image_paths", viewer.loadedImageLabelPaths(),
            "--output_folder", appPath,
            "--prediction_model", modelPath,
            "--proj_string", LocalScene.dataProjection,
            "--prediction_score_threshold", score,
          ];

          forceInitialProgress();

          let importData;

          let exe = new PythonExecutable();
          setTrainClassifyExe(exe);

          exe.run({
            command: commands,
            saveLogs: true,
            onLine: jsonData => {
              updateDownloadingResources(jsonData);

              if (jsonData.progress) {
                setTrainClassifyProgress(jsonData.progress);
              } else {
                importData = jsonData;
                showMessage(`Classification complete, loading detected labels...`, {
                  variant: "success"
                });
              }
            },
            onClose: async () => {
              try {
                await viewer.loadImageLabelData(importData, setLabelLoadProgress);
                showMessage(`Labels Loaded Successfully...`, {
                  variant: "success"
                });
              }
              catch (err) {
                showMessage("Classification Process not completed", {
                  variant: "warning"
                });
              }
              finally {
                resetExecutableValues();
              }
            }
          });
        } catch {
          setClassification(false);
          showMessage(`Error running Classification_model`, {
            variant: "error"
          });
        }
      } catch {
        setClassification(false);
        showMessage(`Error finding trained model file`, {
          variant: "error"
        });
      }
    }).catch(error => {
      console.log(error);
      setClassification(false);
    });
  };

  const canRunTraining = () => {
    let jsonData = viewer?.labelObject();
    const noAnnotations = jsonData.annotations.length === 0;
    const noImages = jsonData.images.length === 0;
    if (noAnnotations || noImages) {
      showMessage(`No training labels found. Active images are required`, {
        variant: "warning"
      });
      return false;
    }

    return true;
  };

  const runTraining = (numIterations) => {
    if (training) {
      showMessage(`Cannot run training last process complete is complete`, {
        variant: "warning"
      });
      return;
    }

    resetExecutableValues();
    setTraining(true);

    let modelPath;
    let cocoPath;
    let outputPath;

    dialog.showOpenDialog({
      properties: ['openDirectory']
    }).then(result => {
      if (result.canceled) {
        setTraining(false);
        return;
      }

      try {
        outputPath = slash(result.filePaths[0]);
        cocoPath = path.join(outputPath, "labels_for_training.3dl");
        let jsonData = viewer?.labelObject();
        let text = JSON.stringify(jsonData, null, 4);

        fs.writeFile(cocoPath, text, 'utf8', (err) => {
          if (err) {
            console.log(err);
            showMessage(`Failed to export labels for training`, {
              variant: "error"
            });
          }
        });

        forceInitialProgress();

        let exe = new PythonExecutable();
        setTrainClassifyExe(exe);

        let pythonName = "aerial_image_training";
        let modelName = path.basename(outputPath);
        let commands = [
          "-p", pythonName,
          "--image_paths", viewer.activeImageLabelPaths(),
          "--output_folder", outputPath,
          "--coco_data_file", cocoPath,
          "--solver_max_iterations", numIterations,
          '--model_title', modelName,
          '--batch_size_per_image', 256,
          "--tiled_image_mode", isOrthoLabel
        ];

        exe.run({
          command: commands,
          saveLogs: true,
          onLine: jsonData => {
            updateDownloadingResources(jsonData);

            if (jsonData.gpu_memory_error) {
              showMessage(`GPU has insufficient memory. Switching to CPU based training`, {
                variant: "warning"
              });
            } else if (jsonData.progress) {
              setTrainClassifyProgress(jsonData.progress);
            } else if (jsonData.model_file_path) {
              modelPath = jsonData.model_file_path;
            } else if (jsonData.url) {
              setTrainingURL(jsonData.url);
              handleViewTraining(jsonData.url);
            }
          },
          onClose: () => {
            try {
              if (modelPath) {
                showMessage(`Training complete, model saved to ${modelPath}`, {
                  variant: "success"
                });
              } else {
                showMessage(`Training incomplete, model may not have saved correctly.`, {
                  variant: "warning"
                });
              }
            }
            catch (err) {
              console.log(err);
              showMessage("Training process not completed", {
                variant: "warning"
              });
            }
            finally {
              resetExecutableValues();
            }
          }
        });
      }
      catch (err ){
        console.log(err);
        setTraining(false);
        showMessage("Error in training", {
          variant: "error"
        });
      }
    }).catch(error => {
      console.log(error);
      setTraining(false);
    });
  };

  useEffect(() => {
    if (!imageLabelOpen) {
      return;
    }

    const onKeyDown = event => {
      if (event.target instanceof HTMLInputElement) {
        return;
      }

      viewer?.labelHotkey(event);
    };

    window.addEventListener('keydown', onKeyDown, false);

    return () => {
      window.removeEventListener('keydown', onKeyDown, false);
    };
  }, [imageLabelOpen]);

  useEffect(() => {
    ipcRenderer?.removeAllListeners("open-label-ortho");
    ipcRenderer?.on("open-label-ortho", () => {
      handleOpen("ortho");
    });

    ipcRenderer?.removeAllListeners("open-label-scene");
    ipcRenderer?.on("open-label-scene", () => {
      handleOpen("scene");
    });
  }, [viewer, imageLabelOpen, labelType]);

  useEffect(() => {
    viewer?.setActiveLabelType(labelType);

    if (isOrthoLabel) {
      dispatch(changeSwitched(true));
      setModalName("Aerial Classification");
    } else if (isSceneLabel) {
      dispatch(changeSwitched(false));
      setModalName("Scene View Classification");
    }
  }, [labelType]);

  useEffect(() => {
    if (imageLabelOpen) {
      setDefaultTab();
    } else {
      if (!minimized) {
        setLabelType(null);
      }
      setMinimized(false);
    }

    setTabsEnabled(!imageLabelOpen);
  }, [viewer, imageLabelOpen]);

  useEffect(() => {
    if (minimized) {
      setImageLabelOpen(false);
    }
  }, [minimized]);

  useEffect(() => {
    if (classification) {
      setPredOrTrain("Classification");
    } else if (training) {
      setPredOrTrain("Training");
    }

    if (trainClassifyProgress === 100) {
      setTrainClassifyProgress(0);
    }
  }, [trainClassifyProgress, classification, training]);

  const isTraining = predOrTrain === "Training";
  const isModified = categoryList.length !== 0;
  const exportDisabled = viewer?.anyLabelSelected();
  const labelDisabled = classification || training || (labelLoadProgress > 0);
  const trainingLinkDisabled = trainingURL === null;

  return (
    <React.Fragment>
      <DraggableDialog
        narrow
        open={imageLabelOpen}
        title={modalName}
        onClose={checkClose}
        onMinimize={handleMinimize}
      >

        {/* Label toolbar icons */}
        <IconToolBar margin={2}>

          {/* Add category */}
          <ToolBarButton
            title={"Add Class"}
            icon={"fa-plus-circle"}
            onClick={() => handleCategoryChange(true)}
            success
          />

          {/* Run Training */}
          <ToolBarButton
            title={"Run Training"}
            icon={"fa-train"}
            onClick={() => {
              if (!canRunTraining()) {
                return;
              }

              setTrainingPrompt(true);
            }}
            disabled={labelDisabled}
          />

          {/* Run Classification */}
          <ToolBarButton
            title={"Run Classification"}
            icon={"fa-object-group"}
            onClick={() => {
              if (!canRunClassification()) {
                return;
              }

              setClassificationPrompt(true);
            }}
            disabled={labelDisabled}
          />

          {/* Import label file */}
          <ToolBarButton
            title={"Import Labels"}
            icon={"fa-upload"}
            onClick={importImageLabels}
            disabled={exportDisabled}
          />

          {/* Export Label file */}
          <ToolBarButton
            title={"Export Labels"}
            icon={"fa-download"}
            onClick={() => exportJSON(categoryList)}
            disabled={exportDisabled}
          />

          {/* Export to DXF */}
          <ToolBarButton
            title={"Export Labels to DXF"}
            visible={isOrthoLabel}
            icon={"fa-pencil-square-o"}
            onClick={() => {
              if (!canExportDXF()) {
                return;
              }

              runExportDXF();
            }}
          />

        </IconToolBar>

        <DenseDivider variant="middle" spacing={5}/>

        {(trainClassifyProgress > 0) && (<React.Fragment>

          <div className={classes.progressToolbar}>

            <ArrowTooltip title={downloadingResources ? "" : `Running ${predOrTrain} On Dataset`}>

              {/** Training/Classification progress */}
              <div className={classes.toolbarLinear} style={{
                flex: isTraining ? 8 : 9,
              }}>
                <LinearProgressWithLabel
                  className={classes.toolbarProgressCenter}
                  variant="determinate"
                  value={trainClassifyProgress}
                  color="secondary"
                />
              </div>

            </ArrowTooltip>

            {/** Training/Classification cancel button */}
            {isTraining && (
              <ToolBarButton
                title={`View Training Progress`}
                icon={"fa-line-chart"}
                disabled={trainingLinkDisabled}
                onClick={() => {
                  handleViewTraining(trainingURL);
                }}
              />
            )}

            {/** Training/Classification cancel button */}
            <ToolBarButton
              title={`Cancel ${predOrTrain}`}
              icon={"fa-ban"}
              onClick={handleCancelProgress}
              error
            />
          </div>


        </React.Fragment>)}

        {downloadingResources && (
          <Typography className={classes.downloadMargin} variant="caption" align="center">
            Downloading Resources... <CircularProgress size="1em"/>
          </Typography>

        )}

        <DialogContent>
          <List className={classes.formControl}>
            {categoryList.map(category =>
              <Category
                key={category.id}
                viewer={viewer}
                category={category}
                isValidCategory={isValidCategory}
              />)}
          </List>
        </DialogContent>

      </DraggableDialog>

      <TextDialog
        open={addCategory}
        onCancel={handleCancel}
        onSubmit={handleAddCategory}
        title="Create New Class"
        prompt="Enter new class name"
        default=''
        label="Class Name"
        clear={true}
      />

      {/* Close modal */}
      <PromptDialog
        open={closePrompt}
        onCancel={() => setClosePrompt(false)}
        onSubmit={() => {
          handleClose();
          setClosePrompt(false);
        }}
        title="Close Classification Window?"
        prompt={`Do you want to close the ${modalName} window? All created labels will be lost.`}
        button="Close"
      />

      <PromptDialog
        open={importPrompt}
        onCancel={() => setImportData(null)}
        onSubmit={() => handleImport()}
        title="Import Image Labels"
        prompt={`Do you want to import ${importCategories?.length} ${labelType} classes
          (${importCategories?.join(", ")}) and ${numImportLabels} labels?. Connected labels
          will be merged together`}
        button="Import Labels"
      />

      {/* Set training parameters */}
      <TextDialog
        open={trainingPrompt}
        onCancel={() => setTrainingPrompt(false)}
        onSubmit={handleTrainingPrompt}
        title="Select training model parameters"
        prompt="Select the number of iterations for training your model file."
        default='250'
        label="Training Iterations"
      />

      {/* Set classification parameters */}
      <TextDialog
        open={classificationPrompt}
        onCancel={() => setClassificationPrompt(false)}
        onSubmit={handleClassificationPrompt}
        title="Select classification parameters"
        prompt="Select the classification score, as a decimal between 0 - 1."
        default='0.5'
        label="Classification Score"
      />

      {/* Image label import percent dialog */}
      <ProgressDialog
        open={labelLoadProgress > 0}
        text={"Importing Image Labels..."}
        percent={labelLoadProgress}
        noTransition
      />

    </React.Fragment>
  );
};